import Sentry from "@similarweb/sentry-logger";

import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { createTimeStringFromDuration } from "app/pages/benchmark-tracker/helpers/dateHelpers";
import { createBenchmarkTrackerApi } from "app/pages/benchmark-tracker/services/api";
import type { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import { benchmarkTrackerTracking } from "app/pages/benchmark-tracker/services/tracking";
import * as ac from "app/pages/benchmark-tracker/store/actions/report";
import { getGAMetaDataEffect } from "app/pages/benchmark-tracker/store/effects/report";
import { selectIsDigestSubscribed } from "app/pages/benchmark-tracker/store/selectors/reportSelectors";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PdfExportService } from "app/services/PdfExportService";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

export const downloadPdfEffect = (domain?: string) => async (dispatch: ThunkDispatchCommon) => {
    const track = benchmarkTrackerTracking.report;
    const { duration } = proNavigator.getParams();

    dispatch(ac.downloadPdf.request());

    const date = createTimeStringFromDuration(duration);

    try {
        await PdfExportService.downloadPdfFedService("wwo.report.link", "bt.home.page.title", {
            domain,
            date,
        });

        track.trackPdfDownload();
        dispatch(ac.downloadPdf.success());
    } catch (e) {
        Sentry.error("Error downloading pdf", e);
        dispatch(ac.downloadPdf.failure(e));
    }
};

export const subscribeToDigestEffect =
    (benchmarkId: string, email: string, benchmarkName: string, granularity: BenchmarkGranularity) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const api = createBenchmarkTrackerApi();
        const state = getState();
        const isDigestSubscribed = selectIsDigestSubscribed(state);

        const successToastText = isDigestSubscribed
            ? translate("bt.subscribe.toast.changes.success")
            : translate("bt.subscribe.toast.success", { email, benchmarkName });

        try {
            dispatch(ac.subscribeToDigest.request());
            await api.report.subscribeToDigest(benchmarkId, granularity);

            dispatch(getGAMetaDataEffect(benchmarkId));
            dispatch(ac.closeSubscriptionModal());
            dispatch(ac.subscribeToDigest.success());
            dispatch(showSuccessToast(successToastText));
        } catch (error) {
            Sentry.errorTracker({
                message: "BenchmarkTracker: Error when subscribing to digest",
                e: error,
                level: "error",
                params: {
                    extra: {
                        benchmarkId
                    }
                }
            });
            dispatch(ac.subscribeToDigest.failure(error));
            dispatch(
                showErrorToast(
                    translate("bt.subscription.toast.error", {
                        benchmarkName,
                        action: "subscribing",
                    }),
                ),
            );
        }
    };

export const unsubscribeFromDigestEffect =
    (benchmarkId: string, benchmarkName: string) => async (dispatch: ThunkDispatchCommon) => {
        const api = createBenchmarkTrackerApi();

        try {
            dispatch(ac.unsubscribeFromDigest.request());
            await api.report.unsubscribeFromDigest(benchmarkId);

            dispatch(getGAMetaDataEffect(benchmarkId));
            dispatch(ac.closeSubscriptionModal());
            dispatch(ac.unsubscribeFromDigest.success());
            dispatch(showSuccessToast(translate("bt.unsubscribe.toast.success", { benchmarkName })));
        } catch (error) {
            Sentry.errorTracker({
                message: "BenchmarkTracker: Error when unsubscribing to digest",
                e: error,
                level: "error",
                params: {
                    extra: {
                        benchmarkId
                    }
                }
            });
            dispatch(ac.unsubscribeFromDigest.failure(error));
            dispatch(
                showErrorToast(
                    translate("bt.subscription.toast.error", {
                        benchmarkName,
                        action: "unsubscribing",
                    }),
                ),
            );
        }
    };
